import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Statistic, Badge, Typography, Tag, Space } from 'antd';
import { QuestionCircleOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { IQuestionTemplate } from '../../services/nestjs-base-api';
import { getColorForCategory } from './questionTemplateColors';
import { getDisplayName } from '../../utils/elementDisplayNames';

const { Title } = Typography;

interface CategoryOverviewProps {
    templates: IQuestionTemplate[];
}

const CategoryOverview: React.FC<CategoryOverviewProps> = ({ templates }) => {
    const intl = useIntl();
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const { categoryStats, categoryQuestions } = useMemo(() => {
        const stats: { [key: string]: { total: number; core: number; active: number } } = {};
        const questions: { [key: string]: IQuestionTemplate[] } = {};
        
        templates.forEach(template => {
            if (!stats[template.category]) {
                stats[template.category] = { total: 0, core: 0, active: 0 };
                questions[template.category] = [];
            }
            
            stats[template.category].total++;
            questions[template.category].push(template);
            
            if (template.isCore) {
                stats[template.category].core++;
            }
            if (template.status === 'active') {
                stats[template.category].active++;
            }
        });
        
        // Sort questions within each category by code
        Object.keys(questions).forEach(category => {
            questions[category].sort((a, b) => a.code.localeCompare(b.code));
        });
        
        return { categoryStats: stats, categoryQuestions: questions };
    }, [templates]);

    const toggleCategory = (category: string) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(category)) {
            newExpanded.delete(category);
        } else {
            newExpanded.add(category);
        }
        setExpandedCategories(newExpanded);
    };

    const renderQuestionText = (text: string) => {
        const parts = text.split(/({{brand}})/);
        return (
            <span>
                {parts.map((part, index) => 
                    part === '{{brand}}' ? (
                        <Tag key={index} color="blue" style={{ margin: '0 2px', fontSize: '11px' }}>
                            {'{{brand}}'}
                        </Tag>
                    ) : (
                        <span key={index}>{part}</span>
                    )
                )}
            </span>
        );
    };


    const totalQuestions = templates.length;
    const coreQuestions = templates.filter(t => t.isCore).length;
    const activeQuestions = templates.filter(t => t.status === 'active').length;

    return (
        <Card>
            <Title level={4} style={{ marginBottom: 24 }}>
                <QuestionCircleOutlined style={{ marginRight: 8 }} />
                {intl.formatMessage({ id: 'categoryOverview.title' })}
            </Title>
            
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col span={8}>
                    <Statistic
                        title={intl.formatMessage({ id: 'categoryOverview.stats.totalStatements' })}
                        value={totalQuestions}
                        valueStyle={{ color: '#F59E0B' }}
                    />
                </Col>
                <Col span={8}>
                </Col>
                <Col span={8}>
                </Col>
            </Row>
            
            <Row gutter={[16, 16]}>
                {Object.entries(categoryStats)
                    .sort(([a], [b]) => getDisplayName(a).localeCompare(getDisplayName(b)))
                    .map(([category, stats]) => {
                        const isExpanded = expandedCategories.has(category);
                        const categoryIndex = Object.keys(categoryStats).indexOf(category);
                        const categoryColor = getColorForCategory(category, categoryIndex);
                        
                        return (
                            <Col key={category} xs={24} sm={isExpanded ? 24 : 12} md={isExpanded ? 24 : 8} lg={isExpanded ? 24 : 6}>
                                <Card 
                                    size="small"
                                    bordered
                                    style={{ 
                                        borderTop: `3px solid ${categoryColor}`,
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onClick={() => toggleCategory(category)}
                                    hoverable
                                >
                                    <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Badge 
                                            color={categoryColor} 
                                            text={<strong>{getDisplayName(category)}</strong>}
                                        />
                                        {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: isExpanded ? 16 : 16 }}>
                                        <div>{intl.formatMessage({ id: 'categoryOverview.category.total' }, { count: stats.total })}</div>
                                    </div>
                                    
                                    {isExpanded && (
                                        <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                {categoryQuestions[category].map((question) => (
                                                    <div 
                                                        key={question.code}
                                                        style={{ 
                                                            padding: '8px 12px', 
                                                            background: '#fafafa', 
                                                            borderRadius: '6px',
                                                            border: '1px solid #e8e8e8'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                            <Tag color="blue" style={{ marginTop: 2 }}>{question.code}</Tag>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontSize: '13px', lineHeight: '1.5', paddingTop: 3 }}>
                                                                    {renderQuestionText(question.text)}
                                                                </div>
                                                                <div style={{ marginTop: 4 }}>
                                                                    {question.isCore && <Tag color="green" style={{ fontSize: '11px' }}>{intl.formatMessage({ id: 'categoryOverview.tag.core' })}</Tag>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </Space>
                                        </div>
                                    )}
                                </Card>
                            </Col>
                        );
                    })}
            </Row>
        </Card>
    );
};

export default CategoryOverview;